/**
 * LOS DOS BOTONES DE RETAR, en el perfil de otra persona.
 *
 * VAN AL LADO DE «CONTACTAR» Y NO EN SU LUGAR. El de WhatsApp sigue estando:
 * es el que la gente ya usa y el que resuelve el partido de esta tarde. Estos
 * dos son lo mismo CON MEMORIA — queda anotado que se propuso, que dijeron
 * que sí, y quién ganó.
 *
 *  · «Retar amistosamente» está siempre. Es a cualquiera.
 *  · «Retar por ranking» aparece SOLO si los dos comparten club y ranking, y
 *    eso NO lo decide esta pantalla: lo decide el servidor y llega hecho en
 *    `player.rankedChallengeIn`. La regla —mismo club, misma categoría, mismo
 *    sexo, los dos activos— vive en `ChallengesService`, con sus pruebas. Si
 *    acá se volviera a escribir para decidir si dibujar el botón, habría dos
 *    copias: la que decide de verdad y la que decide qué se ve. El día que
 *    una cambie aparece el peor de los botones, uno que se puede apretar y
 *    contesta que no.
 *
 * CON DOS CLUBES EN COMÚN, SE PREGUNTA EN CUÁL. Los puntos son de un club:
 * elegir el primero por la aplicación le daría a alguien puntos en un ranking
 * que nunca eligió, y se enteraría al ver la tabla.
 *
 * DESPUÉS DE MANDARLO, EL BOTÓN NO VUELVE. Queda el aviso de que se mandó y
 * el enlace a «Mis retos»: tocarlo de nuevo daría el error de «ya hay uno
 * abierto», que es un error correcto contestando una pregunta que la pantalla
 * no debió dejar hacer.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type {
  ChallengeKind,
  ChallengePlayer,
  PublicPlayer,
  PublicPlayerClub,
} from '@tennisup/contracts';

import { Alert } from './Alert';
import { PlayerPicker } from './PlayerPicker';
import { Select } from './Select';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { useAuth } from '../auth/AuthContext';

export function ChallengeButtons({ player }: { player: PublicPlayer }): JSX.Element {
  const { t } = useTranslation();
  const { session } = useAuth();
  const [enviando, setEnviando] = useState<ChallengeKind | null>(null);
  const [mandado, setMandado] = useState<ChallengeKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dónde = player.rankedChallengeIn;
  /** Con uno solo no hay nada que preguntar: ese es. */
  const [club, setClub] = useState<string>(() => dónde[0]?.communityId ?? '');

  /*
    SINGLES O DOBLES, y las dos parejas.

    Arranca en singles porque es lo que se reta casi siempre: quien abre el
    perfil de alguien para retarlo no tiene por qué contestar «¿de a cuántos?»
    antes de poder tocar el botón.
  */
  const [dobles, setDobles] = useState(false);
  const [miPareja, setMiPareja] = useState<ChallengePlayer | null>(null);
  const [suPareja, setSuPareja] = useState<ChallengePlayer | null>(null);

  /** En dobles hacen falta las dos parejas: un dobles de tres no es un reto. */
  const listo = !dobles || (miPareja !== null && suPareja !== null);

  /**
   * Quiénes ya están en el partido, menos el campo que se está eligiendo. Ver
   * la nota de `PlayerPicker`: es lo que impide ofrecer a alguien repetido.
   */
  function yaEnElPartido(salvo: ChallengePlayer | null): string[] {
    return [
      session?.user.id ?? null,
      player.userId,
      miPareja?.userId ?? null,
      suPareja?.userId ?? null,
    ].filter((id): id is string => id !== null && id !== salvo?.userId);
  }

  async function retar(kind: ChallengeKind): Promise<void> {
    setError(null);
    setEnviando(kind);
    try {
      await api.createChallenge({
        toUserId: player.userId,
        kind,
        ...(kind === 'RANKING' ? { communityId: club } : {}),
        // Las dos o ninguna: `listo` ya garantizó que en dobles están las dos.
        ...(dobles && miPareja !== null && suPareja !== null
          ? { doubles: { myPartnerId: miPareja.userId, theirPartnerId: suPareja.userId } }
          : {}),
      });
      setMandado(kind);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setEnviando(null);
    }
  }

  if (mandado !== null) {
    return (
      <Alert kind="success">
        {t('challenge.sent', { name: player.displayName })}{' '}
        <Link to="/retos?v=mine">{t('challenge.seeMine')}</Link>
      </Alert>
    );
  }

  return (
    <div className="challenge-buttons">
      {error !== null && <Alert kind="error">{error}</Alert>}

      {/*
        DOBLES ES UNA CASILLA Y NO DOS BOTONES MÁS. Con botones serían cuatro
        —amistoso, amistoso en dobles, ranking, ranking en dobles— y la
        pantalla pasaría a preguntar dos cosas a la vez. La casilla dice «de a
        cuántos» y los botones siguen diciendo «de qué tipo»: son preguntas
        distintas y se leen mejor separadas.
      */}
      <label className="check">
        <input
          type="checkbox"
          checked={dobles}
          disabled={enviando !== null}
          onChange={(e) => setDobles(e.target.checked)}
        />
        {t('challenge.doubles')}
      </label>

      {dobles && (
        <>
          <PlayerPicker
            id="challenge-mi-pareja"
            label={t('challenge.myPartner')}
            chosen={miPareja}
            onChoose={setMiPareja}
            exclude={yaEnElPartido(miPareja)}
          />
          <PlayerPicker
            id="challenge-su-pareja"
            label={t('challenge.theirPartner', { name: player.displayName })}
            chosen={suPareja}
            onChoose={setSuPareja}
            exclude={yaEnElPartido(suPareja)}
          />
        </>
      )}

      <div className="challenge-actions">
        <button
          type="button"
          className="secondary"
          disabled={enviando !== null || !listo}
          onClick={() => void retar('FRIENDLY')}
        >
          {enviando === 'FRIENDLY' ? t('common.sending') : t('challenge.friendly')}
        </button>

        {dónde.length > 0 && (
          <button
            type="button"
            className="secondary"
            disabled={enviando !== null || club === '' || !listo}
            onClick={() => void retar('RANKING')}
          >
            {enviando === 'RANKING' ? t('common.sending') : t('challenge.ranked')}
          </button>
        )}
      </div>

      {/* EL DESPLEGABLE SOLO CON DOS O MÁS. Con uno, preguntar «¿en cuál?»
          cuando hay una sola respuesta es un paso de más que además hace
          dudar de si hay otra opción escondida. */}
      {dónde.length > 1 && (
        <div className="field">
          <label htmlFor="challenge-where">{t('challenge.whereItCounts')}</label>
          <Select
            id="challenge-where"
            value={club}
            onChange={setClub}
            options={dónde.map((c: PublicPlayerClub) => ({
              value: c.communityId,
              label: c.communityName,
              hint: t(`categoryLevel.${c.level}`),
            }))}
          />
        </div>
      )}

      <p className="help">
        {dónde.length > 0 ? t('challenge.rankedHelp') : t('challenge.friendlyHelp')}
      </p>
    </div>
  );
}
